import React, { useState, useMemo } from 'react';
import { Pencil, Plus, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
    IconButton,
    Box,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Paper,
    InputAdornment,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    Select,
    MenuItem
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import useGroups from '../../../hooks/purchaseInvoices/useGroups.js';
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';
import {
    StyledContainer,
    StyledCard,
} from "../../../styles/purchaseInvoices/pages/commonTableStyles.js";
import ConfirmationDialog from '../../../components/purchaseInvoices/pages/ConfirmationDialog.js';
import groupsStyles from '../../../styles/purchaseInvoices/pages/groupsStyles.js';

const EMPTY_GROUP = {
    name: '',
    email: '',
    inboxEmail: '',
    defaultApprover: '',
    isActive: true,
    autoApprovalEnabled: false,
    autoApprovalMatchThreshold: 90,
    limitAmountEnabled: false,
    autoApprovalMaxAmount: '',
    autoApprovalDelayHours: 0,
    requiresAttentionDaysThreshold: 3,
    useOrderBasedValidation: false,
    requireApprovalByReasonCode: false,
    parentGroupName: null,
    isParentGroup: false,
    allowViewAllInvoices: false,
    managerNotificationEmail: ''
};

const GROUP_TYPE_OPTIONS = [
    { value: 'standalone', label: 'קבוצה עצמאית', icon: '👤', desc: 'קבוצה בלי תלות' },
    { value: 'parent', label: 'קבוצת אב', icon: '🏢', desc: 'מקבצת קבוצות-בן' },
    { value: 'child', label: 'קבוצת בן', icon: '🔗', desc: 'תחת קבוצת אב' },
];

const Groups = () => {
    const { isDesktop } = useResponsive();
    const [expandedRow, setExpandedRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [collapsedParents, setCollapsedParents] = useState(new Set());

    const {
        groups,
        isLoading,
        error,
        addGroup,
        updateGroup,
        deleteGroup
    } = useGroups();

    const [addOpen, setAddOpen] = useState(false);
    const [addGroupType, setAddGroupType] = useState('standalone');
    const [editOpen, setEditOpen] = useState(false);
    const [editGroupType, setEditGroupType] = useState('standalone');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [newGroup, setNewGroup] = useState({ ...EMPTY_GROUP });
    const [editGroup, setEditGroup] = useState(null);

    const handleEdit = (group) => {
        setEditGroup({
            ...group,
            limitAmountEnabled: group.autoApprovalMaxAmount !== undefined &&
                group.autoApprovalMaxAmount !== null &&
                group.autoApprovalMaxAmount !== ''
        });
        const type = group.isParentGroup ? 'parent' : group.parentGroupName ? 'child' : 'standalone';
        setEditGroupType(type);
        setEditOpen(true);
    };

    const handleDeleteClick = (groupId, groupName) => {
        setGroupToDelete({ _id: groupId, name: groupName });
        setDeleteOpen(true);
    };

    const handleCancelDelete = () => {
        setDeleteOpen(false);
        setGroupToDelete(null);
    };

    const handleConfirmDelete = async () => {
        await deleteGroup(groupToDelete._id);
        setDeleteOpen(false);
        setGroupToDelete(null);
    };

    const closeAddDialog = () => {
        setNewGroup({ ...EMPTY_GROUP });
        setAddGroupType('standalone');
        setAddOpen(false);
    };

    const potentialParents = useMemo(() =>
        [...new Set((groups || [])
            .filter(g => g.isParentGroup || (!g.parentGroupName && !g.isParentGroup))
            .map(g => g.name))].sort(),
        [groups]
    );

    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim() || !groups?.length) return groups || [];
        const lower = searchTerm.toLowerCase();
        return groups.filter(g =>
            g.name.toLowerCase().includes(lower) ||
            (g.email && g.email.toLowerCase().includes(lower)) ||
            (g.parentGroupName && g.parentGroupName.toLowerCase().includes(lower))
        );
    }, [groups, searchTerm]);

    const { tableRows, parentDocs } = useMemo(() => {
        const parentDocsMap = {};
        const childrenByParent = {};
        const standalone = [];

        const explicitParents = new Set(filteredGroups.filter(g => g.isParentGroup).map(g => g.name));
        const referencedParents = new Set(filteredGroups.filter(g => g.parentGroupName).map(g => g.parentGroupName));
        const allParentNames = new Set([...explicitParents, ...referencedParents]);

        filteredGroups.forEach(g => { if (g.isParentGroup) parentDocsMap[g.name] = g; });
        allParentNames.forEach(name => { if (!childrenByParent[name]) childrenByParent[name] = []; });

        filteredGroups.forEach(g => {
            if (g.parentGroupName) {
                if (!childrenByParent[g.parentGroupName]) childrenByParent[g.parentGroupName] = [];
                childrenByParent[g.parentGroupName].push(g);
            } else if (!allParentNames.has(g.name)) {
                standalone.push(g);
            }
        });

        const rows = [];
        standalone.forEach(g => rows.push({ type: 'standalone', group: g }));
        [...allParentNames].sort().forEach(name => {
            const children = childrenByParent[name] || [];
            rows.push({ type: 'parent', name, group: parentDocsMap[name] || null, childCount: children.length });
            children.forEach(child => rows.push({ type: 'child', group: child }));
        });

        return { tableRows: rows, parentDocs: parentDocsMap };
    }, [filteredGroups]);

    const toggleParent = (name) => {
        setCollapsedParents(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const renderDesktopRow = (item) => {
        if (item.type === 'parent') {
            return (
                <Box key={`parent-${item.name}`} sx={groupsStyles.parentBannerRow} onClick={() => toggleParent(item.name)}>
                    <Box sx={groupsStyles.parentBannerLeft}>
                        <IconButton size="small" sx={groupsStyles.collapseButton}>
                            {collapsedParents.has(item.name) ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                        </IconButton>
                        <Typography sx={groupsStyles.parentBannerName}>{item.name}</Typography>
                        <Chip label={`${item.childCount} תת-קבוצות`} size="small" sx={groupsStyles.parentBannerChip} />
                    </Box>
                    {parentDocs[item.name] && (
                        <Box sx={groupsStyles.parentBannerActions}>
                            <IconButton
                                size="small"
                                onClick={e => { e.stopPropagation(); handleEdit(parentDocs[item.name]); }}
                                sx={groupsStyles.editButtonIcon}
                            >
                                <Pencil size={16} color="#1976d2" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={e => { e.stopPropagation(); handleDeleteClick(parentDocs[item.name]._id, item.name); }}
                                sx={groupsStyles.deleteButtonIcon}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            );
        }

        const group = item.group;
        const isChild = item.type === 'child';

        if (isChild && collapsedParents.has(group.parentGroupName)) return null;

        return (
            <Box
                key={group._id}
                sx={isChild
                    ? { ...groupsStyles.cardRow, ...groupsStyles.childCardRow }
                    : groupsStyles.cardRow}
            >
                {/* שם קבוצה */}
                <Box sx={groupsStyles.cardCell}>
                    <Typography variant="body2" fontWeight={600}>{group.name}</Typography>
                </Box>

                {/* מיילים */}
                <Box sx={groupsStyles.cardCell}>
                    {group.email
                        ? <Typography variant="body2" sx={groupsStyles.emailMonospace}>{group.email}</Typography>
                        : <Typography variant="body2" color="text.disabled">—</Typography>
                    }
                    {group.inboxEmail && (
                        <Typography variant="caption" sx={{ ...groupsStyles.emailMonospace, color: '#64748b' }}>
                            {group.inboxEmail}
                        </Typography>
                    )}
                </Box>

                {/* מאשר דיפולטיבי */}
                <Box sx={groupsStyles.cardCell}>
                    <Typography variant="body2">{group.defaultApprover || '—'}</Typography>
                </Box>

                <Box sx={groupsStyles.cardCell}>
                    <Typography variant="caption" component="span" sx={group.isActive ? groupsStyles.activePill : groupsStyles.inactivePill}>
                        {group.isActive ? 'פעילה' : 'לא פעילה'}
                    </Typography>
                    {!group.autoApprovalEnabled ? (
                        <Typography variant="caption" sx={{ color: '#b0b0b0', mt: 0.5 }}>כבוי</Typography>
                    ) : (
                        <Box sx={groupsStyles.autoApprovalBlock}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                {(group.autoApprovalMaxAmount && group.autoApprovalMaxAmount > 0)
                                    ? `עד ${group.autoApprovalMaxAmount.toLocaleString()} ₪`
                                    : 'ללא הגבלת סכום'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#555' }}>
                                שיוך: <b>{group.autoApprovalMatchThreshold}%</b>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#555' }}>
                                {group.autoApprovalDelayHours > 0
                                    ? `השהיה: ${group.autoApprovalDelayHours}ש'`
                                    : 'ללא השהיה'}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={groupsStyles.cardCell}>
                    <Typography variant="body2" sx={groupsStyles.attentionDays}>
                        {group.requiresAttentionDaysThreshold || 3} ימים
                    </Typography>
                </Box>

                {/* פעולות */}
                <Box sx={{ ...groupsStyles.cardCell, flexDirection: 'row', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleEdit(group)} sx={groupsStyles.editButtonIcon}>
                        <Pencil size={16} color="#1976d2" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(group._id, group.name)} sx={groupsStyles.deleteButtonIcon}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        );
    };

    // ---- Shared form fields (Add + Edit), used for standalone & child types ----
    const renderGroupFields = (groupState, setGroupState) => (
        <>
            <div>
                <label style={groupsStyles.formLabel}>מייל ראשי:</label>
                <input
                    type="email"
                    placeholder="הזן מייל ראשי"
                    style={groupsStyles.formInput}
                    value={groupState.email || ''}
                    onChange={(e) => setGroupState(prev => ({ ...prev, email: e.target.value }))}
                />
            </div>

            <div>
                <label style={groupsStyles.formLabel}>תיבת מייל לקבלת חשבוניות:</label>
                <input
                    type="email"
                    placeholder="למשל: invoices-supplier@neopharmgroup.com"
                    style={groupsStyles.formInput}
                    value={groupState.inboxEmail || ''}
                    onChange={(e) => setGroupState(prev => ({ ...prev, inboxEmail: e.target.value }))}
                />
            </div>

            <div>
                <label style={groupsStyles.formLabel}>מייל מנהל לקבלת התראות קליטה:</label>
                <input
                    type="email"
                    placeholder="למשל: manager@neopharmgroup.com"
                    style={groupsStyles.formInput}
                    value={groupState.managerNotificationEmail || ''}
                    onChange={(e) => setGroupState(prev => ({ ...prev, managerNotificationEmail: e.target.value }))}
                />
            </div>

            <div>
                <label style={groupsStyles.formLabel}>מאשר דיפולטיבי:</label>
                <input
                    type="text"
                    placeholder="הזן שם משתמש Priority של המאשר"
                    style={groupsStyles.formInput}
                    value={groupState.defaultApprover || ''}
                    onChange={(e) => setGroupState(prev => ({ ...prev, defaultApprover: e.target.value }))}
                />
            </div>

            <div style={{ margin: '10px 0' }}>
                <label style={{ marginLeft: 8 }}>פעילה:</label>
                <input
                    type="checkbox"
                    checked={groupState.isActive}
                    onChange={e => setGroupState(prev => ({ ...prev, isActive: e.target.checked }))}
                    style={{ transform: 'scale(1.2)' }}
                />
            </div>

            <div style={{ marginTop: '15px' }}>
                <label style={groupsStyles.formLabel}>
                    מספר ימים מקסימלי לטיפול בשגיאה בקליטה לפריוריטי:
                </label>
                <input
                    type="number"
                    min="1"
                    placeholder="3"
                    style={groupsStyles.formInput}
                    value={groupState.requiresAttentionDaysThreshold || 3}
                    onChange={(e) => {
                        const value = Math.max(1, Number(e.target.value));
                        setGroupState(prev => ({ ...prev, requiresAttentionDaysThreshold: value }));
                    }}
                />
            </div>

            <div style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f0f4ff', borderRadius: '8px', border: '1px solid #c8d8ff' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#5c35cc' }}>
                    ⚙️ הגדרות בדיקת חשבוניות
                </Typography>
                <div style={{ margin: '10px 0' }}>
                    <label style={{ marginLeft: 8, fontWeight: 500 }}>
                        בדיקה מול הזמנת רכש ישירה (בלי קבלת סחורה):
                    </label>
                    <input
                        type="checkbox"
                        checked={groupState.useOrderBasedValidation || false}
                        onChange={e => setGroupState(prev => ({ ...prev, useOrderBasedValidation: e.target.checked }))}
                        style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                    />
                </div>
                {groupState.useOrderBasedValidation && (
                    <Typography variant="caption" sx={{ color: '#5c35cc', display: 'block', mt: 0.5, mb: 1 }}>
                        ⚠️ קבוצה זו תבדוק חשבוניות מול מספר הזמנת הרכש (PO) במקום מול קבלת סחורה
                    </Typography>
                )}
            </div>

            <div style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #d0e8ff' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                    הגדרות אישור אוטומטי
                </Typography>

                <div style={{ margin: '10px 0' }}>
                    <label style={{ marginLeft: 8, fontWeight: 500 }}>אפשר אישור אוטומטי:</label>
                    <input
                        type="checkbox"
                        checked={groupState.autoApprovalEnabled}
                        onChange={e => setGroupState(prev => ({ ...prev, autoApprovalEnabled: e.target.checked }))}
                        style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                    />
                </div>

                <div style={{ margin: '10px 0' }}>
                    <label style={{ marginLeft: 8, fontWeight: 500 }}>
                        בדוק קוד סיבת רכש לפני אישור אוטומטי:
                    </label>
                    <input
                        type="checkbox"
                        checked={groupState.requireApprovalByReasonCode || false}
                        onChange={e => setGroupState(prev => ({ ...prev, requireApprovalByReasonCode: e.target.checked }))}
                        style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                    />
                </div>

                {groupState.autoApprovalEnabled && (
                    <div style={{ marginTop: '15px' }}>
                        <label style={groupsStyles.formLabel}>
                            אחוזי התאמה מינימליים לשיוך שורות (%):
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            placeholder="90"
                            style={groupsStyles.formInput}
                            value={groupState.autoApprovalMatchThreshold}
                            onChange={(e) => {
                                const value = Math.min(100, Math.max(0, Number(e.target.value)));
                                setGroupState(prev => ({ ...prev, autoApprovalMatchThreshold: value }));
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            💡 אם כל שורות החשבונית יתאימו מעל <strong>{groupState.autoApprovalMatchThreshold}%</strong> - תאושר אוטומטית
                        </Typography>

                        <div style={{ marginTop: '15px' }}>
                            <label style={groupsStyles.formLabel}>
                                <input
                                    type="checkbox"
                                    checked={groupState.limitAmountEnabled}
                                    onChange={e => setGroupState(prev => ({
                                        ...prev,
                                        limitAmountEnabled: e.target.checked,
                                        autoApprovalMaxAmount: e.target.checked ? prev.autoApprovalMaxAmount : ''
                                    }))}
                                    style={{ marginLeft: '8px', transform: 'scale(1.2)' }}
                                />
                                הגבל סכום לאישור אוטומטי
                            </label>
                            {groupState.limitAmountEnabled && (
                                <>
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="הכנס סכום (₪)"
                                        style={groupsStyles.formInput}
                                        value={groupState.autoApprovalMaxAmount}
                                        onChange={(e) => {
                                            const value = Math.max(0, Number(e.target.value));
                                            setGroupState(prev => ({ ...prev, autoApprovalMaxAmount: value }));
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        💡 חשבוניות מעל סכום זה יעברו לאישור ידני
                                    </Typography>
                                </>
                            )}
                        </div>

                        <div style={{ marginTop: '15px' }}>
                            <label style={groupsStyles.formLabel}>
                                זמן השהיה לפני אישור אוטומטי (בשעות):
                            </label>
                            <input
                                type="number"
                                min="0"
                                placeholder="0"
                                style={groupsStyles.formInput}
                                value={groupState.autoApprovalDelayHours || 0}
                                onChange={(e) => {
                                    const value = Math.max(0, Number(e.target.value));
                                    setGroupState(prev => ({ ...prev, autoApprovalDelayHours: value }));
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                אם תוגדר השהיה, החשבונית תאושר אוטומטית רק לאחר הזמן הזה (בשעות) אם לא אושרה ידנית.
                            </Typography>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    // ---- Group-type selector (shared by Add + Edit) ----
    const renderTypeSelector = (currentType, setType, setGroupState) => (
        <Box sx={groupsStyles.typeSelectorWrap}>
            <Typography variant="caption" sx={groupsStyles.typeSelectorLabel}>סוג הקבוצה</Typography>
            <Box sx={groupsStyles.typeSelectorOptions}>
                {GROUP_TYPE_OPTIONS.map(opt => (
                    <Box
                        key={opt.value}
                        onClick={() => {
                            setType(opt.value);
                            if (opt.value !== 'child') {
                                setGroupState(prev => ({ ...prev, parentGroupName: null }));
                            }
                        }}
                        sx={{
                            flex: 1,
                            minWidth: '120px',
                            p: 1.5,
                            borderRadius: '8px',
                            border: currentType === opt.value ? '2px solid #1976d2' : '2px solid #e0e0e0',
                            backgroundColor: currentType === opt.value ? '#e3f2fd' : 'white',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            textAlign: 'center',
                            '&:hover': { borderColor: '#1976d2', backgroundColor: '#f0f7ff' }
                        }}
                    >
                        <Typography sx={{ fontSize: '1.2rem', mb: 0.25 }}>{opt.icon}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: currentType === opt.value ? 700 : 500, color: currentType === opt.value ? '#1976d2' : '#333' }}>
                            {opt.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>{opt.desc}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    const renderParentSelector = (groupState, setGroupState, excludeName) => (
        <Box sx={groupsStyles.parentSelectorWrap}>
            <label style={{ ...groupsStyles.formLabel, color: '#e65100' }}>בחר קבוצת אב *:</label>
            <Select
                fullWidth
                size="small"
                displayEmpty
                value={groupState.parentGroupName || ''}
                onChange={(e) => setGroupState(prev => ({ ...prev, parentGroupName: e.target.value }))}
                sx={{ mt: 0.5, direction: 'rtl', backgroundColor: 'white' }}
            >
                <MenuItem value="" disabled>בחר קבוצת אב</MenuItem>
                {potentialParents
                    .filter(name => name !== excludeName)
                    .map(name => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))
                }
            </Select>
        </Box>
    );

    // ---- Mobile card ----
    const renderMobileCard = (group) => {
        const isExpanded = expandedRow === group._id;
        return (
            <Paper key={group._id} elevation={1} sx={groupsStyles.mobileCard}>
                <Box sx={groupsStyles.mobileCardHeader}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{group.name}</Typography>
                        <Typography variant="caption" component="span" sx={group.isActive ? groupsStyles.activePill : groupsStyles.inactivePill}>
                            {group.isActive ? 'פעילה' : 'לא פעילה'}
                        </Typography>
                    </Box>
                    <Box sx={groupsStyles.mobileCardButtonsContainer}>
                        <IconButton aria-label="edit" onClick={() => handleEdit(group)} size="small" sx={groupsStyles.editButtonIcon}>
                            <Pencil size={18} color="#1976d2" />
                        </IconButton>
                        <IconButton aria-label="delete" onClick={() => handleDeleteClick(group._id, group.name)} size="small" sx={groupsStyles.deleteButtonIcon}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body2" color="text.secondary">מייל ראשי:</Typography>
                    <Typography variant="body1">{group.email || '-'}</Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">תיבת מייל ייעודית לחשבוניות:</Typography>
                    <Typography variant="body1">{group.inboxEmail || '-'}</Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">מייל מנהל להתראות קליטה:</Typography>
                    <Typography variant="body1">{group.managerNotificationEmail || '-'}</Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">מאשר דיפולטיבי:</Typography>
                    <Typography variant="body1">{group.defaultApprover || '-'}</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <IconButton
                        size="small"
                        onClick={() => setExpandedRow(isExpanded ? null : group._id)}
                        sx={groupsStyles.expandButton}
                    >
                        {isExpanded ? <ChevronUp /> : <ChevronDown />}
                    </IconButton>
                    <Collapse in={isExpanded} timeout="auto">
                        <Box sx={groupsStyles.expandedContent}>
                            <Typography variant="body2" color="text.secondary">סף ימים להתראה:</Typography>
                            <Typography variant="body1" sx={groupsStyles.attentionDays}>
                                {group.requiresAttentionDaysThreshold || 3} ימים
                            </Typography>

                            {group.useOrderBasedValidation && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary">בדיקה מול הזמנת רכש (אלדן):</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>פעיל</Typography>
                                </Box>
                            )}

                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">אישור אוטומטי:</Typography>
                                {!group.autoApprovalEnabled ? (
                                    <Typography variant="body1" sx={{ color: '#b0b0b0' }}>כבוי</Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            {(group.autoApprovalMaxAmount && group.autoApprovalMaxAmount > 0)
                                                ? `עד ${group.autoApprovalMaxAmount.toLocaleString()} ₪`
                                                : 'ללא הגבלת סכום'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#555' }}>
                                            שיוך שורות: <span style={{ fontWeight: 'bold' }}>{group.autoApprovalMatchThreshold}%</span>
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#555' }}>
                                            {group.autoApprovalDelayHours > 0 ? `השהיה: ${group.autoApprovalDelayHours} שעות` : 'ללא השהיה'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
            </Paper>
        );
    };

    if (isLoading) {
        return (
            <Box sx={groupsStyles.loadingContainer}>
                <CircularProgress />
                <Typography variant="h6" color="textSecondary" sx={groupsStyles.loadingText}>טוען נתונים...</Typography>
            </Box>
        );
    }

    return (
        <StyledContainer>
            <StyledCard>
                <Box sx={groupsStyles.headerBox}>
                    <Box sx={{ width: { xs: '100%', sm: '300px' } }}>
                        <TextField
                            placeholder="חיפוש קבוצה"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'primary.main' }} />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ dir: "rtl" }}
                            sx={groupsStyles.searchInput}
                        />
                    </Box>

                    <Button
                        onClick={() => {
                            setNewGroup({ ...EMPTY_GROUP });
                            setAddGroupType('standalone');
                            setAddOpen(true);
                        }}
                        color="primary"
                        size="medium"
                        startIcon={<Plus size={20} strokeWidth={3} />}
                        sx={groupsStyles.addButtonResponsive}
                    >
                        הוספת קבוצה
                    </Button>
                </Box>

                {error ? (
                    <Box sx={groupsStyles.errorBox}>
                        <Typography variant="h6" color="error" sx={groupsStyles.errorText}>שגיאה בטעינת הנתונים</Typography>
                        <Button variant="contained" color="primary" onClick={() => window.location.reload()}>נסה שנית</Button>
                    </Box>
                ) : isDesktop ? (
                    <Box sx={groupsStyles.desktopCardContainer}>
                        <Box sx={groupsStyles.columnHeaderRow}>
                            <Typography sx={groupsStyles.columnHeaderCell}>שם קבוצה</Typography>
                            <Typography sx={groupsStyles.columnHeaderCell}>מיילים</Typography>
                            <Typography sx={groupsStyles.columnHeaderCell}>מאשר דיפולטיבי</Typography>
                            <Typography sx={groupsStyles.columnHeaderCell}>סטטוס / הגבלות</Typography>
                            <Typography sx={groupsStyles.columnHeaderCell}>סף ימים להתראה</Typography>
                            <Typography sx={groupsStyles.columnHeaderCell}>פעולות</Typography>
                        </Box>

                        {tableRows.length === 0 ? (
                            <Box sx={groupsStyles.emptyBox}>
                                <Typography variant="body1" color="text.secondary">
                                    {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין קבוצות להצגה"}
                                </Typography>
                            </Box>
                        ) : (
                            tableRows.map((item) => renderDesktopRow(item))
                        )}
                    </Box>
                ) : (
                    <Box sx={groupsStyles.mobileCardContainer}>
                        {filteredGroups.length === 0 ? (
                            <Box sx={groupsStyles.emptyBox}>
                                <Typography variant="body1" color="text.secondary">
                                    {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין קבוצות להצגה"}
                                </Typography>
                            </Box>
                        ) : (
                            filteredGroups.map(group => renderMobileCard(group))
                        )}
                    </Box>
                )}

                <Box sx={groupsStyles.footerBox}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        {filteredGroups.length > 0 ? `סה"כ ${filteredGroups.length} קבוצות` : ''}
                    </Typography>
                </Box>
            </StyledCard>

            {/* ===== ADD DIALOG ===== */}
            <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="md" fullWidth PaperProps={{ sx: groupsStyles.dialog }}>
                <DialogTitle sx={groupsStyles.dialogTitle}>
                    <Typography sx={groupsStyles.dialogTitleText}>הוספת קבוצה חדשה</Typography>
                    <IconButton onClick={closeAddDialog} sx={groupsStyles.dialogCloseButton} size="small">
                        <X size={20} strokeWidth={2.5} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={groupsStyles.dialogContent}>
                    <div style={groupsStyles.formContainer}>

                        {renderTypeSelector(addGroupType, setAddGroupType, setNewGroup)}

                        {addGroupType === 'child' && renderParentSelector(newGroup, setNewGroup, null)}

                        <div>
                            <label style={groupsStyles.formLabel}>שם קבוצה:</label>
                            <input
                                type="text"
                                placeholder="שם הקבוצה"
                                style={groupsStyles.formInput}
                                value={newGroup.name}
                                onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>

                        {addGroupType === 'parent' && (
                            <Box sx={groupsStyles.parentInfoBox}>
                                <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 500 }}>
                                    💡 קבוצת אב משמשת ככותרת ויזואלית בלבד — אין צורך בהגדרות נוספות.
                                </Typography>
                                <div style={{ marginTop: '12px' }}>
                                    <label style={{ marginLeft: 8, fontWeight: 500, color: '#1565c0' }}>
                                        אפשר למשתמשים לצפות בכל חשבוניות הקבוצה:
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={newGroup.allowViewAllInvoices || false}
                                        onChange={e => setNewGroup(prev => ({ ...prev, allowViewAllInvoices: e.target.checked }))}
                                        style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                                    />
                                </div>
                            </Box>
                        )}
                        {addGroupType !== 'parent' && renderGroupFields(newGroup, setNewGroup)}

                        <div style={groupsStyles.dialogActions}>
                            <Button onClick={closeAddDialog} sx={groupsStyles.dialogCancelButton}>ביטול</Button>
                            <Button
                                onClick={async () => {
                                    const payload = {
                                        ...newGroup,
                                        isParentGroup: addGroupType === 'parent',
                                        ...(addGroupType === 'parent' && {
                                            email: null,
                                            inboxEmail: null,
                                            defaultApprover: null,
                                            autoApprovalEnabled: false,
                                            parentGroupName: null
                                        })
                                    };
                                    await addGroup(payload);
                                    closeAddDialog();
                                }}
                                variant="contained"
                                sx={groupsStyles.dialogSubmitButton}
                            >
                                הוסף
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: groupsStyles.dialog }}>
                <DialogTitle sx={groupsStyles.dialogTitle}>
                    <Typography sx={groupsStyles.dialogTitleText}>עריכת קבוצה</Typography>
                    <IconButton onClick={() => setEditOpen(false)} sx={groupsStyles.dialogCloseButton} size="small">
                        <X size={20} strokeWidth={2.5} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={groupsStyles.dialogContent}>
                    {editGroup && (
                        <div style={groupsStyles.formContainer}>

                            {renderTypeSelector(editGroupType, setEditGroupType, setEditGroup)}

                            {editGroupType === 'child' && renderParentSelector(editGroup, setEditGroup, editGroup.name)}

                            <div>
                                <label style={groupsStyles.formLabel}>שם קבוצה:</label>
                                <input
                                    type="text"
                                    placeholder="שם הקבוצה"
                                    style={groupsStyles.formInput}
                                    value={editGroup.name}
                                    onChange={(e) => setEditGroup(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>

                            {editGroupType === 'parent' && (
                                <Box sx={groupsStyles.parentInfoBox}>
                                    <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 500 }}>
                                        💡 קבוצת אב משמשת ככותרת ויזואלית בלבד — אין צורך בהגדרות נוספות.
                                    </Typography>
                                    <div style={{ marginTop: '12px' }}>
                                        <label style={{ marginLeft: 8, fontWeight: 500, color: '#1565c0' }}>
                                            אפשר למשתמשים לצפות בכל חשבוניות הקבוצה:
                                        </label>
                                        <input
                                            type="checkbox"
                                            checked={editGroup.allowViewAllInvoices || false}
                                            onChange={e => setEditGroup(prev => ({ ...prev, allowViewAllInvoices: e.target.checked }))}
                                            style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                                        />
                                    </div>
                                </Box>
                            )}
                            {editGroupType !== 'parent' && renderGroupFields(editGroup, setEditGroup)}

                            <div style={groupsStyles.dialogActions}>
                                <Button onClick={() => setEditOpen(false)} sx={groupsStyles.dialogCancelButton}>ביטול</Button>
                                <Button
                                    onClick={async () => {
                                        const payload = {
                                            ...editGroup,
                                            isParentGroup: editGroupType === 'parent',
                                            ...(editGroupType === 'parent' && {
                                                email: null,
                                                inboxEmail: null,
                                                defaultApprover: null,
                                                autoApprovalEnabled: false,
                                                parentGroupName: null
                                            })
                                        };
                                        await updateGroup(editGroup._id, payload);
                                        setEditOpen(false);
                                        setEditGroup(null);
                                    }}
                                    variant="contained"
                                    sx={groupsStyles.dialogSubmitButton}
                                >
                                    עדכון
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                entityName={groupToDelete?.name || ""}
                entityType="קבוצה"
                open={deleteOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isPending={false}
            />
        </StyledContainer>
    );
};

export default Groups;